// Global Instructions Rule Applied!
//
// Configuration → Pipelines detail: manage one pipeline's stages. Every
// per-stage setting (colour override, visibility, age counters, who can move
// records into or out of the stage, max-age and the days-in-stage thresholds +
// colours) is edited inline in the table — switches/colours/pickers commit on
// change, numbers on blur/Enter. "Who can move" is a user right
// (pipeline_stage.move, docs/USER_RIGHTS_DESIGN.md) and replaced the board-only
// Drop switch (pipeline_config.drop_option_kb, no longer read). Reorder, remove
// (guarded while it holds deals) and add (existing library stage or a new one)
// round out the panel.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, Modal, Popconfirm, Radio, Select, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import type { SelectProps } from 'antd'
import { ArrowLeftOutlined, ArrowUpOutlined, ArrowDownOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useLookups } from '../../global_configs/hooks/useLookups.ts'
import type { PipelineStageAdminModel } from '../../../state_manager/data_models/pipeline_stage_admin_model.ts'
import type { StageLibraryModel } from '../../../state_manager/data_models/stage_library_model.ts'
import {
  fetchPipelineStages,
  fetchStageLibrary,
  createStage,
  createPipelineConfig,
  updatePipelineConfig,
  deletePipelineConfig
} from '../services/pipelinesConfigService.ts'
import { refreshBoardConfigs } from '../services/refreshBoardConfigs.ts'
import { fetchRoles, fetchCapabilityOverrides, saveCapabilityOverride } from '../services/userRightsConfigService.ts'
import type { RoleModel, CapabilityOverridesModel } from '../../../state_manager/data_models/user_rights_admin_model.ts'
import { CAPABILITY_PIPELINE_STAGE_MOVE } from '../../../core/hooks/useStageMoveRights.ts'
import { nextMoveRoleIds } from '../utils/moveRoleSelection.ts'
import ColourPickerField from './ColourPickerField.tsx'
import InlineNumberField from './InlineNumberField.tsx'
import StageEditModal from './StageEditModal.tsx'

const { Title, Text } = Typography

interface PipelineDetailPanelProps {
  pipelineId: string
  pipelineName: string
  onBack: () => void
}

const token = () => getAccessToken(store.getState().auth)

function ColourDot({ colour }: { colour: string | null | undefined }): React.ReactElement {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: colour || '#D1D5DB',
        border: '0.5px solid rgba(0,0,0,0.15)',
        verticalAlign: 'middle'
      }}
    />
  )
}

export default function PipelineDetailPanel({ pipelineId, pipelineName, onBack }: PipelineDetailPanelProps): React.ReactElement {
  const [stages, setStages] = useState<PipelineStageAdminModel[]>([])
  const [library, setLibrary] = useState<StageLibraryModel[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)

  // Edit-stage modal (the SHARED library row, same component the Pipeline
  // Stages panel uses — so an edit reached from here carries the same
  // used-by warning and category filtering).
  const [editStageId, setEditStageId] = useState<string | null>(null)

  // Add-stage modal
  const [addOpen, setAddOpen] = useState(false)
  const [addMode, setAddMode] = useState<'existing' | 'new'>('existing')
  const [addExistingId, setAddExistingId] = useState<string | null>(null)
  const [newStage, setNewStage] = useState<{ name: string; type: string | null; decision_status: string | null; colour: string | null }>({
    name: '',
    type: null,
    decision_status: null,
    colour: null
  })

  // Who may move records into or out of each stage (capability
  // pipeline_stage.move, one override per pipeline_config row; the default rule
  // covers every stage without one). docs/USER_RIGHTS_DESIGN.md 6.1.
  const [roles, setRoles] = useState<RoleModel[]>([])
  const [stageMoveRules, setStageMoveRules] = useState<CapabilityOverridesModel | null>(null)
  const [savingMoveRule, setSavingMoveRule] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [s, lib, r, rules] = await Promise.all([
        fetchPipelineStages(pipelineId, token()),
        fetchStageLibrary(token()),
        fetchRoles(token()),
        fetchCapabilityOverrides(CAPABILITY_PIPELINE_STAGE_MOVE, token())
      ])
      if (s.error) showErrorToast(s.error)
      if (r.error) showErrorToast(r.error)
      if (rules.error) showErrorToast(rules.error)
      setStages(s.data)
      setLibrary(lib.data)
      setRoles(r.data ?? [])
      setStageMoveRules(rules.data)
    } finally {
      setLoading(false)
    }
  }, [pipelineId])

  // Admins pass every rule, so the picker offers Everyone and the other roles
  // only; an empty choice is "admins only".
  const everyoneRoleId = useMemo(() => roles.find((r) => r.roles__is_everyone)?.roles__id ?? null, [roles])
  const moveRoleOptions = useMemo(
    () =>
      roles
        .filter((r) => !r.roles__is_superuser)
        .sort((a, b) => Number(b.roles__is_everyone) - Number(a.roles__is_everyone) || a.roles__sort_order - b.roles__sort_order)
        .map((r) => ({ value: r.roles__id, label: r.roles__name })),
    [roles]
  )

  const stageMoveRoleIds = useCallback(
    (configId: string): string[] => {
      if (!stageMoveRules) return []
      const override = stageMoveRules.prog__overrides.find((o) => o.capability_rules__scope_id === configId)
      return override ? override.prog__role_ids : stageMoveRules.prog__default_role_ids
    },
    [stageMoveRules]
  )

  const saveStageMoveRule = useCallback(
    async (configId: string, next: string[]) => {
      const roleIds = nextMoveRoleIds(stageMoveRoleIds(configId), next, everyoneRoleId)
      setSavingMoveRule(configId)
      try {
        const res = await saveCapabilityOverride(CAPABILITY_PIPELINE_STAGE_MOVE, configId, roleIds, token())
        if (res.error || !res.data) {
          showErrorToast(res.error || 'Failed to save who can move')
          load()
          return
        }
        const saved = res.data
        setStageMoveRules((prev) =>
          prev
            ? {
                ...prev,
                prog__overrides: [
                  ...prev.prog__overrides.filter((o) => o.capability_rules__scope_id !== configId),
                  ...(saved.prog__is_override
                    ? [{ capability_rules__scope_id: configId, prog__role_ids: saved.prog__role_ids }]
                    : [])
                ]
              }
            : prev
        )
        // The signed-in admin's own rights (boards, stage menus) come from /global-configs.
        refreshBoardConfigs()
      } finally {
        setSavingMoveRule(null)
      }
    },
    [stageMoveRoleIds, everyoneRoleId, load]
  )

  useEffect(() => {
    load()
  }, [load])

  // Inline persist for a single pipeline_config setting: optimistic local update
  // then PATCH; on failure refetch (revert), on success refresh the board cache.
  const patchStage = useCallback(
    async (row: PipelineStageAdminModel, aliasField: keyof PipelineStageAdminModel, columnField: string, value: unknown) => {
      setStages((prev) =>
        prev.map((r) =>
          r.pipeline_config__id === row.pipeline_config__id ? ({ ...r, [aliasField]: value } as PipelineStageAdminModel) : r
        )
      )
      const res = await updatePipelineConfig(row.pipeline_config__id, { [columnField]: value }, token())
      if (!res.success) {
        showErrorToast(res.error || 'Failed to save')
        load()
      } else {
        refreshBoardConfigs()
      }
    },
    [load]
  )

  // Mark a stage as the pipeline's default. The DB trigger clears the other
  // defaults, so optimistically reflect that (this row true, the rest false).
  const setDefaultStage = useCallback(
    async (row: PipelineStageAdminModel) => {
      if (row.pipeline_config__default) return
      setStages((prev) =>
        prev.map(
          (r) =>
            ({ ...r, pipeline_config__default: r.pipeline_config__id === row.pipeline_config__id } as PipelineStageAdminModel)
        )
      )
      const res = await updatePipelineConfig(row.pipeline_config__id, { default: true }, token())
      if (!res.success) {
        showErrorToast(res.error || 'Failed to set default stage')
        load()
      } else {
        refreshBoardConfigs()
      }
    },
    [load]
  )

  const typeOptions = useMemo(() => {
    const seen = new Map<string, string>()
    library.forEach((s) => {
      if (s.pipeline_stages__type) seen.set(s.pipeline_stages__type, s.pipeline_stages__type__label || s.pipeline_stages__type)
    })
    return Array.from(seen, ([value, label]) => ({ value, label }))
  }, [library])

  // Canonical status list from cfg.pipeline_statuses (via the synthesized
  // lookup groups), grouped by pipeline kind — the stage library dedupe is
  // only the fallback for a payload that has not loaded yet, so the picker no
  // longer hides statuses that exist but are unused.
  const { getLookupOptions } = useLookups()
  // Grouped when the lookup lists exist, flat from the library otherwise —
  // both are shapes antd's Select accepts.
  const decisionOptions = useMemo<SelectProps['options']>(() => {
    const groups = [
      { label: 'Deal Origination', options: getLookupOptions('Deal Origination Status') },
      { label: 'Deal Execution', options: getLookupOptions('Deal Execution Status') },
      { label: 'Fundraising', options: getLookupOptions('Fundraising Status') }
    ].filter((g) => g.options.length > 0)
    if (groups.length > 0) return groups
    const seen = new Map<string, string>()
    library.forEach((s) => {
      if (s.pipeline_stages__decision_status)
        seen.set(s.pipeline_stages__decision_status, s.pipeline_stages__decision_status__label || s.pipeline_stages__decision_status)
    })
    return Array.from(seen, ([value, label]) => ({ value, label }))
  }, [getLookupOptions, library])

  const availableToAdd = useMemo(() => {
    const inUse = new Set(stages.map((s) => s.pipeline_config__stage))
    return library.filter((s) => !inUse.has(s.pipeline_stages__id))
  }, [stages, library])

  const nextSequence = useMemo(() => {
    const max = stages.reduce((m, s) => Math.max(m, s.pipeline_config__sequence_kb ?? 0), 0)
    return max + 1
  }, [stages])

  const reorder = useCallback(
    async (index: number, dir: -1 | 1) => {
      const target = index + dir
      if (target < 0 || target >= stages.length) return
      const a = stages[index]
      const b = stages[target]
      const seqA = a.pipeline_config__sequence_kb ?? index + 1
      const seqB = b.pipeline_config__sequence_kb ?? target + 1
      setBusy(true)
      try {
        // Write BOTH orderings. sequence_tbl is not decorative — the deals
        // table, the mobile list and the agent deal/prospect scopes order by
        // it, so moving a stage on the board while leaving sequence_tbl behind
        // silently desynchronises those views from the kanban.
        const [r1, r2] = await Promise.all([
          updatePipelineConfig(a.pipeline_config__id, { sequence_kb: seqB, sequence_tbl: seqB }, token()),
          updatePipelineConfig(b.pipeline_config__id, { sequence_kb: seqA, sequence_tbl: seqA }, token())
        ])
        if (!r1.success || !r2.success) {
          showErrorToast(r1.error || r2.error || 'Failed to reorder')
          return
        }
        load()
        refreshBoardConfigs()
      } finally {
        setBusy(false)
      }
    },
    [stages, load]
  )

  const openStageEdit = useCallback((stageId: string | null | undefined) => setEditStageId(stageId ?? null), [])

  const editingStage = useMemo(
    () => library.find((s) => s.pipeline_stages__id === editStageId) || null,
    [library, editStageId]
  )

  const removeStage = useCallback(
    async (row: PipelineStageAdminModel) => {
      const result = await deletePipelineConfig(row.pipeline_config__id, pipelineId, token())
      if (!result.success) {
        showErrorToast(result.error || 'Failed to remove stage')
        return
      }
      toast.success('Stage removed from pipeline')
      load()
      refreshBoardConfigs()
    },
    [pipelineId, load]
  )

  const submitAdd = useCallback(async () => {
    setBusy(true)
    try {
      let stageId = addExistingId
      if (addMode === 'new') {
        if (!newStage.name.trim()) {
          showErrorToast('Stage name is required')
          return
        }
        if (!newStage.type || !newStage.decision_status) {
          showErrorToast('Stage type and decision status are required')
          return
        }
        const created = await createStage(
          { name: newStage.name.trim(), type: newStage.type, decision_status: newStage.decision_status, colour: newStage.colour },
          token()
        )
        if (!created.success) {
          showErrorToast(created.error || 'Failed to create stage')
          return
        }
        stageId = (created.data as { pipeline_stages__id?: string } | undefined)?.pipeline_stages__id || null
      }
      if (!stageId) {
        showErrorToast('Select a stage to add')
        return
      }
      const attached = await createPipelineConfig(
        { pipeline: pipelineId, stage: stageId, sequence_kb: nextSequence, sequence_tbl: nextSequence },
        token()
      )
      if (!attached.success) {
        showErrorToast(attached.error || 'Failed to add stage')
        return
      }
      toast.success('Stage added to pipeline')
      setAddOpen(false)
      setAddExistingId(null)
      setNewStage({ name: '', type: null, decision_status: null, colour: null })
      load()
      refreshBoardConfigs()
    } finally {
      setBusy(false)
    }
  }, [addMode, addExistingId, newStage, pipelineId, nextSequence, load])

  const columns: ColumnsType<PipelineStageAdminModel> = useMemo(
    () => [
      {
        title: 'Order',
        key: 'order',
        width: 70,
        fixed: 'left',
        render: (_: unknown, _row, index) => (
          <Space size={0}>
            <Button type="text" size="small" disabled={index === 0 || busy} icon={<ArrowUpOutlined />} onClick={() => reorder(index, -1)} />
            <Button
              type="text"
              size="small"
              disabled={index === stages.length - 1 || busy}
              icon={<ArrowDownOutlined />}
              onClick={() => reorder(index, 1)}
            />
          </Space>
        )
      },
      {
        title: 'Stage',
        key: 'stage',
        width: 210,
        fixed: 'left',
        render: (_: unknown, row) => (
          <Space>
            <ColourDot colour={row.pipeline_config__colour || row.pipeline_stages__colour} />
            <span className="font-medium">{row.pipeline_stages__name}</span>
            <Tooltip title="Edit the shared stage (name, decision status, colour)">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openStageEdit(row.pipeline_config__stage)}
              />
            </Tooltip>
          </Space>
        )
      },
      {
        // Read-only here: the decision status belongs to the SHARED stage, so
        // it is edited through the stage modal (which warns about the other
        // pipelines using it), not inline per pipeline.
        title: (
          <Tooltip title="Belongs to the shared stage — drives follower eligibility and status reporting">
            Decision status
          </Tooltip>
        ),
        key: 'decision_status',
        width: 150,
        render: (_: unknown, row) => {
          const lib = library.find((s) => s.pipeline_stages__id === row.pipeline_config__stage)
          const label = lib?.pipeline_stages__decision_status__label as string | undefined
          return label ? <Tag color="blue">{label}</Tag> : <Text type="secondary">—</Text>
        }
      },
      {
        title: <Tooltip title="The default stage — new deals enter here. Exactly one per pipeline.">Default</Tooltip>,
        key: 'default',
        width: 80,
        align: 'center',
        render: (_: unknown, row) => (
          <Radio checked={row.pipeline_config__default ?? false} onChange={() => setDefaultStage(row)} />
        )
      },
      {
        title: <Tooltip title="Kanban colour override for this stage">Colour</Tooltip>,
        key: 'colour',
        width: 70,
        align: 'center',
        render: (_: unknown, row) => (
          <ColourPickerField
            value={row.pipeline_config__colour ?? null}
            onChange={(c) => patchStage(row, 'pipeline_config__colour', 'colour', c)}
          />
        )
      },
      {
        title: 'Visible',
        key: 'visible',
        width: 75,
        align: 'center',
        render: (_: unknown, row) => (
          <Switch
            size="small"
            checked={row.pipeline_config__visible_kb ?? true}
            onChange={(v) => patchStage(row, 'pipeline_config__visible_kb', 'visible_kb', v)}
          />
        )
      },
      {
        title: <Tooltip title="Show the days-in-stage / days-in-pipeline chips on cards in this stage">Counters</Tooltip>,
        key: 'counters',
        width: 85,
        align: 'center',
        render: (_: unknown, row) => (
          <Switch
            size="small"
            checked={row.pipeline_config__show_age_counters ?? true}
            onChange={(v) => patchStage(row, 'pipeline_config__show_age_counters', 'show_age_counters', v)}
          />
        )
      },
      {
        // Replaces the board-only Drop switch: who may move a record into or out
        // of this stage, everywhere it can be moved (capability pipeline_stage.move).
        title: (
          <Tooltip title="Who may move deals or prospects into or out of this stage - on the boards, the record, the grids and the New drawers. Admins always may; empty means admins only.">
            Who can move
          </Tooltip>
        ),
        key: 'who_can_move',
        width: 190,
        render: (_: unknown, row) => (
          <Select
            size="small"
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Admins only"
            disabled={!stageMoveRules || savingMoveRule === row.pipeline_config__id}
            loading={savingMoveRule === row.pipeline_config__id}
            options={moveRoleOptions}
            value={stageMoveRoleIds(row.pipeline_config__id)}
            onChange={(next: string[]) => saveStageMoveRule(row.pipeline_config__id, next)}
          />
        )
      },
      {
        title: <Tooltip title="Board age window (days)">Max age</Tooltip>,
        key: 'max_age',
        width: 90,
        align: 'center',
        render: (_: unknown, row) => (
          <InlineNumberField
            value={row.pipeline_config__max_age_kb ?? null}
            onCommit={(v) => patchStage(row, 'pipeline_config__max_age_kb', 'max_age_kb', v)}
          />
        )
      },
      {
        title: <Tooltip title="Days in stage before the warn colour applies">Warn</Tooltip>,
        key: 'warn',
        width: 120,
        render: (_: unknown, row) => (
          <Space size={4}>
            <InlineNumberField
              value={row.pipeline_config__stage_warn_days ?? null}
              onCommit={(v) => patchStage(row, 'pipeline_config__stage_warn_days', 'stage_warn_days', v)}
            />
            <ColourPickerField
              value={row.pipeline_config__stage_warn_colour ?? null}
              onChange={(c) => patchStage(row, 'pipeline_config__stage_warn_colour', 'stage_warn_colour', c)}
            />
          </Space>
        )
      },
      {
        title: <Tooltip title="Days in stage before the critical colour applies">Crit</Tooltip>,
        key: 'crit',
        width: 120,
        render: (_: unknown, row) => (
          <Space size={4}>
            <InlineNumberField
              value={row.pipeline_config__stage_crit_days ?? null}
              onCommit={(v) => patchStage(row, 'pipeline_config__stage_crit_days', 'stage_crit_days', v)}
            />
            <ColourPickerField
              value={row.pipeline_config__stage_crit_colour ?? null}
              onChange={(c) => patchStage(row, 'pipeline_config__stage_crit_colour', 'stage_crit_colour', c)}
            />
          </Space>
        )
      },
      {
        title: 'Deals',
        dataIndex: 'pipeline_config__deal_count',
        key: 'deals',
        width: 70,
        align: 'right',
        render: (n: number | null) => <Text type={n ? undefined : 'secondary'}>{n ?? 0}</Text>
      },
      {
        title: '',
        key: 'actions',
        width: 60,
        align: 'center',
        fixed: 'right',
        render: (_: unknown, row) => (
          <Popconfirm
            title="Remove this stage from the pipeline?"
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={() => removeStage(row)}
          >
            <Tooltip title="Remove from pipeline">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        )
      }
    ],
    // `library` belongs here: the Decision status column reads it, and it
    // loads alongside the stages — without it the column renders from a stale
    // closure and stays blank after the first paint.
    [
      busy,
      stages.length,
      reorder,
      patchStage,
      setDefaultStage,
      removeStage,
      library,
      openStageEdit,
      stageMoveRules,
      savingMoveRule,
      moveRoleOptions,
      stageMoveRoleIds,
      saveStageMoveRule
    ]
  )

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {pipelineName} — stages
          </Title>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
          Add stage
        </Button>
      </div>

      <div className="flex-1 min-h-0">
        <Table<PipelineStageAdminModel>
          rowKey="pipeline_config__id"
          columns={columns}
          dataSource={stages}
          loading={loading}
          pagination={false}
          size="middle"
          scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
        />
      </div>

      {/* Add-stage modal */}
      <Modal
        title="Add stage to pipeline"
        open={addOpen}
        okText="Add"
        confirmLoading={busy}
        onOk={submitAdd}
        onCancel={() => setAddOpen(false)}
        destroyOnClose
      >
        <Radio.Group
          value={addMode}
          onChange={(e) => setAddMode(e.target.value)}
          options={[
            { label: 'Existing stage', value: 'existing' },
            { label: 'New stage', value: 'new' }
          ]}
          optionType="button"
          style={{ marginBottom: 16 }}
        />
        {addMode === 'existing' ? (
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select a stage from the library"
            style={{ width: '100%' }}
            value={addExistingId || undefined}
            onChange={(v) => setAddExistingId(v)}
            options={availableToAdd.map((s) => ({ value: s.pipeline_stages__id, label: s.pipeline_stages__name || s.pipeline_stages__id }))}
          />
        ) : (
          <div className="flex flex-col gap-3">
            <Input
              placeholder="Stage name"
              value={newStage.name}
              maxLength={120}
              onChange={(e) => setNewStage({ ...newStage, name: e.target.value })}
            />
            <Select
              placeholder="Stage type"
              style={{ width: '100%' }}
              value={newStage.type || undefined}
              onChange={(v) => setNewStage({ ...newStage, type: v })}
              options={typeOptions}
            />
            <Select
              placeholder="Decision status"
              style={{ width: '100%' }}
              value={newStage.decision_status || undefined}
              onChange={(v) => setNewStage({ ...newStage, decision_status: v })}
              options={decisionOptions}
            />
            <div className="flex items-center justify-between">
              <Text>Colour</Text>
              <ColourPickerField value={newStage.colour} onChange={(c) => setNewStage({ ...newStage, colour: c })} />
            </div>
          </div>
        )}
      </Modal>

      <StageEditModal
        open={!!editStageId}
        stage={editingStage}
        onClose={() => setEditStageId(null)}
        onSaved={() => {
          load()
          refreshBoardConfigs()
        }}
      />
    </div>
  )
}
